import type { Moment } from 'moment';
import moment from 'moment';

export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const DATE_ONLY_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * API date fields (`target_purchase_date`, etc.) are `YYYY-MM-DD` without a timezone.
 * Parsing with `new Date('YYYY-MM-DD')` or `moment(iso)` treats them as UTC and can show the wrong
 * calendar day in US timezones. Use the helpers in this file instead of ad hoc conversion.
 */

/**
 * Parses API date-only strings (YYYY-MM-DD) as local calendar dates.
 * @param {string} value - Date-only or ISO-prefixed string from the API.
 * @returns {Date | null} Local calendar date, or null when not date-only.
 */
export const parseCalendarDateString = (value: string): Date | null => {
  const trimmed = value.trim();
  const match = DATE_ONLY_PREFIX.exec(trimmed);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value?: string | Date | null): string => {
  if (value == null || value === '') return '-';
  const date = typeof value === 'string' ? parseCalendarDateString(value) ?? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

/** Unix ms for table date-range filters (same local-calendar rules as {@link formatDate}). */
export const calendarDateToTimestamp = (value?: string | null): number | null => {
  if (value == null || value === '') return null;
  const local = parseCalendarDateString(value);
  if (local) return local.getTime();
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
};

/** Ant Design DatePicker → `YYYY-MM-DD` for POST/PATCH (no `toISOString()` timezone shift). */
export const formatApiDateOnly = (value: Moment): string => value.format('YYYY-MM-DD');

const normalizeApiDateInput = (value: string | Date): string => {
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value).trim();
};

/**
 * GET /deals date field → DatePicker value (local calendar day).
 * Accepts `YYYY-MM-DD`, Prisma-style ISO datetimes (`2026-06-15T00:00:00.000Z`), and `Date` instances.
 */
export const parseApiDateOnly = (value: string | Date): Moment => {
  const normalized = normalizeApiDateInput(value);
  if (!normalized) {
    return moment.invalid();
  }

  const local = parseCalendarDateString(normalized);
  if (local) {
    return moment(local).startOf('day');
  }

  const utc = moment.utc(normalized);
  if (utc.isValid()) {
    return utc.startOf('day').local();
  }

  const strict = moment(normalized, 'YYYY-MM-DD', true);
  return strict.isValid() ? strict.startOf('day') : moment.invalid();
};

/** Maps GET /deals `target_purchase_date` into a DatePicker value; omits invalid/empty input. */
export const toDealFormTargetDate = (value: string | Date | null | undefined): Moment | undefined => {
  if (value == null || value === '') {
    return undefined;
  }
  const parsed = parseApiDateOnly(value);
  return parsed.isValid() ? parsed : undefined;
};

export const formatEnumLabel = (value?: string | null): string => {
  if (value == null || value === '') return '-';
  return value.replace(/_/g, ' ');
};

export const sortByString = (a: string, b: string): number => a.localeCompare(b, undefined, { sensitivity: 'base' });

export const dash = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

export const yn = (v: boolean): string => (v ? 'Yes' : 'No');
